import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";



/**
 * RoyalCollectRespinCounterController
 * Manages the respin counter spine animation for Royal Collect feature.
 * Uses the hns_counter spine asset.
 * Injected into the bonus_counter slot of the reel_background spine
 * (via RoyalCollectReelFrontController.bonusCounterSlotContainer).
 *
 * Single public method: playAnimation(respinsLeft)
 * - Internally tracks displayed value
 * - Computes transition animation (e.g., counter_3_to_2)
 * - Falls back to static animation (e.g., counter_3) if transition not available
 */
class RoyalCollectRespinCounterController {
  constructor(parentContainer) {
    this.parentContainer = parentContainer;
    this.counterSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Currently displayed value (what's shown on screen)
    this.displayedValue = null;

    // Animation queuing — prevents clashing animations
    this._isAnimating = false;
    this._pendingAnimation = null;

    // Animation name caches
    this._availableAnimations = null;
    this._hasAnimationCache = new Map();

    this.initialize();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return this.counterSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Initialize the counter spine and add to parent container
   */
  initialize() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_counter-skeleton";
      const atlasAlias = "hns_counter-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        console.warn("[RCRespinCounter] Counter spine assets not found");
        return;
      }

      this.counterSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.counterSpine?.skeleton?.data?.animations || !this.counterSpine?.state) {
        console.warn("[RCRespinCounter] Counter spine missing required components");
        return;
      }

      // Cache available animations
      this._availableAnimations = this.counterSpine.skeleton.data.animations.map(
        (a) => a.name
      );

      // Add to parent container (the bonus_counter slot container)
      this.parentContainer.addChild(this.counterSpine);

      // Compensate for the hns_counter spine's internal artboard offset.
      // The 'counter' bone is at y=-257.98 in spine data, which pushes
      // visual content down from root. Negate it so the counter aligns
      // with the bonus_counter bone position in the reel_background spine.
      const counterBone = this.counterSpine.skeleton.findBone("counter");
      const yCompensation = counterBone ? counterBone.data.y : 0;
      this.counterSpine.position.set(0, yCompensation);

      // Warm up spine structure
      this.warmup();

      this.initialized = true;
    } catch (error) {
      console.warn("[RCRespinCounter] Failed to initialize:", error.message);
    }
  }

  /**
   * Warm up spine by setting an animation and clearing it
   */
  warmup() {
    if (!this.counterSpine?.state) return;

    try {
      const startAnim = this._availableAnimations?.find((a) => a === "counter_0") ||
        this._availableAnimations?.[0];

      if (startAnim) {
        this.counterSpine.state.setAnimation(0, startAnim, false);
        this.counterSpine.state.update(0.016);

        try {
          if (this.counterSpine.skeleton.physics !== undefined) {
            this.counterSpine.skeleton.updateWorldTransform();
          }
        } catch (_) {
          // Skip if physics not available
        }
      }

      this.displayedValue = 0;
    } catch (_) {
      // Warmup is optional
    }
  }

  /**
   * Check if an animation exists in this spine
   */
  hasAnimation(name) {
    if (this._hasAnimationCache.has(name)) {
      return this._hasAnimationCache.get(name);
    }

    const exists = this._availableAnimations?.includes(name) || false;
    this._hasAnimationCache.set(name, exists);
    return exists;
  }

  /**
   * Play the animation for the given respins value.
   * This is the single public method for updating the counter display.
   *
   * - If a transition animation exists (e.g., counter_3_to_2), plays it
   * - Otherwise falls back to a static animation (e.g., counter_2)
   * - After any transition TO 3, chains to counter_3 static
   * - Plays sound on every transition
   *
   * @param {number} respinsLeft - The current respins remaining (0-3)
   */
  playAnimation(respinsLeft) {
    if (!this.isReady()) {
      return;
    }

    // Same value — nothing to animate
    if (this.displayedValue === respinsLeft) {
      return;
    }

    // If currently animating, queue only if the target differs from displayed
    // (guards at enqueue site so _executeAnimation never receives a no-op)
    if (this._isAnimating) {
      if (respinsLeft !== this.displayedValue) {
        this._pendingAnimation = respinsLeft;
      }
      return;
    }

    this._executeAnimation(respinsLeft);
  }


  /**
   * Execute an animation immediately (internal — called by playAnimation or queue)
   * @private
   */
  _executeAnimation(respinsLeft) {
    const from = this.displayedValue;

    // First call or no previous value — show static (no transition needed)
    if (from === null || from === undefined) {
      this._showStatic(respinsLeft, true);
      this.displayedValue = respinsLeft;
      this._processQueue();
      return;
    }

    // Try transition animation
    let animName = `counter_${from}_to_${respinsLeft}`;

    // Fallback: counter_1_to_3 doesn't exist in the spine asset,
    // so use counter_0_to_3 instead (visually similar enough)
    if (respinsLeft === 3 && from === 1) {
      animName = "counter_0_to_3";
    }

    if (this.hasAnimation(animName)) {
      this._isAnimating = true;
      this._playTransitionAnim(animName, respinsLeft);
    } else {
      // Fallback to static
      this._showStatic(respinsLeft, false);
      this._processQueue();
    }

    this.displayedValue = respinsLeft;
  }

  /**
   * Process queued animation after current one finishes
   * @private
   */
  _processQueue() {
    this._isAnimating = false;
    if (this._pendingAnimation !== null) {
      const next = this._pendingAnimation;
      this._pendingAnimation = null;
      this._executeAnimation(next);
    }
  }

  /**
   * Play a named transition animation
   * @private
   */
  _playTransitionAnim(animName, targetValue) {
    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);

      soundManager.play(SOUND_NAME.add_respin);

      this.counterSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === animName) {
            // If transitioning TO 3, show static counter_3 first
            if (targetValue === 3) {
              this._showStatic(3, true);
            }
            // Process any queued animation
            this._processQueue();
          }
        },
      });
    } catch (_) {
      // Fallback to static on error
      this._showStatic(targetValue, false);
      this._processQueue();
    }
  }

  /**
   * Show a static counter state (counter_0, counter_1, counter_2, counter_3)
   * @private
   */
  _showStatic(value, silent = false) {
    if (!this.isReady()) return;

    const animName = `counter_${value}`;
    if (!this.hasAnimation(animName)) return;

    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);

      if (!silent) {
        soundManager.play(SOUND_NAME.add_respin);
      }
    } catch (_) {
      // Silent fail
    }
  }



  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.isCleanedUp) return;
    this.isCleanedUp = true;

    if (this.counterSpine) {
      this.counterSpine.state?.clearListeners();
      this.counterSpine.state?.clearTracks();

      if (this.parentContainer?.children?.includes(this.counterSpine)) {
        this.parentContainer.removeChild(this.counterSpine);
      }
      this.counterSpine.destroy();
      this.counterSpine = null;
    }

    this._availableAnimations = null;
    this._hasAnimationCache.clear();
    this.displayedValue = null;
    this.initialized = false;
  }
}

export default RoyalCollectRespinCounterController;
